import React, { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { apiKeyState, designListState, filesClipboardState, toastState } from "../../recoil/filemanager/atoms";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  pasteFilesFromClipboard,
  pasteFoldersFromClipboard,
  renameFolder,
  reorder,
  selectStartToEnd,
  updateNodeProps,
  INTERNAL_FOLDER_PATH,
  deleteFolders,
  getFilesDeep,
  toggleExpandNodes,
} from "../../utils";
import { ContextMenu } from "@blueprintjs/core";
import { clipboardOperations, formatClipboardOperations } from "../../constants";
import TreeNode from "./FolderNode";
import classNames from "classnames";
import { FileManagerAPI } from "../../api";
import FmContextMenu from "./FmContextMenu";
import { DeleteEntryDialog } from "./AlertDialogs";
import { IconNames } from "@alternative/icons";
import { Button } from "@alternative/components";
const DraggableTreeNode = (props) => {
  const { nodeItem, index, nodeLength, children, ...otherProps } = props;
  const { id } = nodeItem;
  const isDragDisabled = nodeLength === 1;
  return (
    <Draggable draggableId={id} key={id} index={index} isDragDisabled={isDragDisabled}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <TreeNode {...otherProps} nodeItem={nodeItem} dragHandleProps={!isDragDisabled && provided.dragHandleProps}>
            {children}
          </TreeNode>
        </div>
      )}
    </Draggable>
  );
};
const FolderTree = () => {
  const [designList, setDesignList] = useRecoilState(designListState);
  const apiKey = useRecoilValue(apiKeyState);
  const [clipBoardItems, setClipboardItems] = useRecoilState(filesClipboardState);
  const { designTree, selectedFolders } = designList;
  const [draggingNodeId, setDraggingNodeId] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ files: [], isOpen: false });
  const [toastListState, setToastLisState] = useRecoilState(toastState);
  const { toasts } = toastListState;
  const handleNodeClick = (nodeItem, currentLevelNodes, e) => {
    e.stopPropagation();
    let newSelectedFolders = [nodeItem];
    const firstsel = selectedFolders[0] || {};
    console.log(nodeItem);
    if (firstsel.level === nodeItem.level || nodeItem.fullPath !== INTERNAL_FOLDER_PATH) {
      if (e.ctrlKey) {
        const selection = [...selectedFolders, nodeItem];
        newSelectedFolders = currentLevelNodes.filter((node) => selection.find((item) => item.id === node.id));
      }
      if (e.shiftKey) {
        if (!selectedFolders.length) {
          newSelectedFolders = [nodeItem];
        } else {
          newSelectedFolders = selectStartToEnd({
            list: currentLevelNodes,
            startNode: selectedFolders[0],
            endNode: nodeItem,
          });
        }
      }
    }
    let updatedTree = designTree;
    if (!e.ctrlKey && !e.shiftKey && !nodeItem.isExpanded) {
      updatedTree = updateNodeProps(designTree, nodeItem, "isExpanded", true).updatedTree;
    }
    setDesignList({ ...designList, selectedFolders: newSelectedFolders, designTree: updatedTree, selectedDesigns: [] });
  };
  const toggleNodeExpand = (node, isExpanded) => {
    const { updatedTree } = updateNodeProps(designTree, node, "isExpanded", isExpanded);
    setDesignList({ ...designList, designTree: updatedTree });
  };
  const handleCut = (e) => {
    setClipboardItems({
      ...clipBoardItems,
      operation: clipboardOperations.CUT,
      items: selectedFolders,
      type: "folder",
    });
  };
  const handleCopy = (e) => {
    setClipboardItems({
      ...clipBoardItems,
      operation: clipboardOperations.COPY,
      items: selectedFolders,
      type: "folder",
    });
  };
  const handlePaste = async (e) => {
    const newItems = [...clipBoardItems.items];
    console.log("handlePaste -> newItems", newItems);
    let updatedList;
    if (clipBoardItems.type === "folder") {
      updatedList = pasteFoldersFromClipboard(designTree, newItems, selectedFolders[0], clipBoardItems.operation);
      const { fileMap, foldersPasted } = updatedList;
      let message = `${formatClipboardOperations(clipBoardItems.operation)} ${fileMap.length} files`;
      let newToast = {
        message,
        canDismiss: false,
      };
      setToastLisState({
        ...toastListState,
        toasts: [...toasts, newToast],
      });
      if (clipBoardItems.operation === clipboardOperations.COPY) {
        const done = await FileManagerAPI.copyFileList({ key: apiKey, fileMap });
        if (!done) return; //TODO: Error State
      }

      if (clipBoardItems.operation === clipboardOperations.CUT) {
        const done = await FileManagerAPI.moveFileList({ key: apiKey, fileMap });
        if (!done) return; //TODO: Error State
        const deleteFolders = foldersPasted.sort((a, b) => {
          if (a.level < b.level) return 1;
          if (a.level > b.level) return -1;
          return 0;
        });
        for (let folder in deleteFolders) {
          const path = folder.fullPath;
          const done = await FileManagerAPI.deleteFolder({ key: apiKey, path });
          if (!done) return;
        }
      }
      message = `${formatClipboardOperations(clipBoardItems.operation, true)} ${fileMap.length} files`;
      setToastLisState({
        ...toastListState,
        toasts: [...toasts, { message, canDismiss: true }],
      });
    } else {
      let clipboardFiles = [...clipBoardItems.items];
      let message = `${formatClipboardOperations(clipBoardItems.operation)} ${clipboardFiles.length} files`;
      let newToast = {
        message,
        canDismiss: false,
      };
      setToastLisState({
        ...toastListState,
        toasts: [...toasts, newToast],
      });
      const fileMap = clipboardFiles.map((clipboardFile) => ({
        OldFile: clipboardFile.fullPath,
        NewFile: `${selectedFolders[0].fullPath}/${clipboardFile.filename}`,
      }));
      let done = false;
      if (clipBoardItems.operation === clipboardOperations.COPY) {
        done = await FileManagerAPI.copyFileList({ key: apiKey, fileMap });
      }
      if (clipBoardItems.operation === clipboardOperations.CUT) {
        done = await FileManagerAPI.moveFileList({ key: apiKey, fileMap });
      }
      if (!done) return;

      message = `${formatClipboardOperations(clipBoardItems.operation, true)} ${clipboardFiles.length} files`;
      setToastLisState({
        ...toastListState,
        toasts: [...toasts, { message, canDismiss: true }],
      });
      updatedList = pasteFilesFromClipboard(designTree, clipboardFiles, selectedFolders[0], clipBoardItems.operation);
    }

    const { updatedTree, updatedNode } = updatedList;
    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDesignList({ ...designList, designTree: updatedTree, selectedFolders: selFolders });
  };
  const handleDelete = async (e) => {
    const updatedList = await deleteFolders({ designTree, apiKey, folders: selectedFolders });
    const { updatedTree, updatedNode } = updatedList;
    setDesignList({
      ...designList,
      designTree: updatedTree,
      selectedFolders: [updatedNode],
    });
  };
  // const handleMoveToInternal = () => {

  // }
  const shouldAllowCopyPaste = () => {
    if (!selectedFolders.length) return false;
    const fp = selectedFolders[0].fullPath.toLowerCase();
    return fp !== "designs" && fp !== INTERNAL_FOLDER_PATH;
  };
  const shouldAllowDelete = () => {
    if (!selectedFolders.length) return false;
    const fp = selectedFolders[0].fullPath.toLowerCase();
    return fp !== "designs" && fp !== INTERNAL_FOLDER_PATH;
  };
  // const shouldAllowMovetoInternal = () => {
  //   if (!selectedFolders.length) return false;
  //   return !selectedFolders[0].isInternal;
  // };
  const handleContextMenu = (e) => {
    e.preventDefault();
    ContextMenu.show(
      <FmContextMenu
        handleCopy={handleCopy}
        handlePaste={handlePaste}
        handleCut={handleCut}
        handleDelete={() => {
          if (!selectedFolders.length) return;
          const files = getFilesDeep(selectedFolders);
          if (!files.length) handleDelete();
          else setDeleteDialog({ files, isOpen: true });
        }}
        // handleMoveToInternal={handleMoveToInternal}
        pasteEnabled={clipBoardItems.items.length}
        deleteEnabled={shouldAllowDelete()}
        copyEnabled={shouldAllowCopyPaste()}
        cutEnabled={shouldAllowCopyPaste()}
        // movetoInternalEnabled={shouldAllowMovetoInternal()}
      />,
      { left: e.clientX, top: e.clientY },
      () => {}
    );
  };
  const handleNodeNameChange = async (nodeItem, value) => {
    const x = await FileManagerAPI.renameFolder({ key: apiKey, oldpath: nodeItem.fullPath, newfoldername: value });
    if (!x) return;
    const { updatedTree, updatedNode } = renameFolder(designTree, nodeItem, value);
    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDesignList({ ...designList, designTree: updatedTree, selectedFolders: selFolders });
  };
  const renderNodes = (parentNode) => {
    const { children: treeNodes, id, isExpanded } = parentNode;
    if (!treeNodes || !treeNodes.length) return null;
    return (
      <DragDropContext
        onDragEnd={async (result) => {
          setDraggingNodeId(null);
          if (!result.destination) return;
          if (result.destination.index === result.source.index) return;
          const reNodes = reorder(treeNodes, result.source.index, result.destination.index);
          const { updatedTree } = updateNodeProps(designTree, parentNode, "children", reNodes);

          const fileOrderFolders = reNodes.map((item) => item.fullPath);
          const fileOrderFiles = parentNode.files.map((item) => item.fullPath);
          const fileOrder = [...fileOrderFolders, ...fileOrderFiles];
          setDesignList({ ...designList, designTree: updatedTree });
          await FileManagerAPI.setFileOrder({ key: apiKey, fileOrder, path: parentNode.fullPath });
        }}
        onBeforeDragStart={(result) => {
          setDraggingNodeId(result.source.droppableId);
        }}
      >
        <Droppable droppableId={id}>
          {(provided) => (
            <div
              className={classNames("drag-node", {
                "drag-node--dragging": draggingNodeId === id,
                "drag-node--collapsed": !isExpanded,
              })}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <ul className="at-tree-node-list">
                {treeNodes
                  .filter((item) => item.fullPath.toLowerCase() !== INTERNAL_FOLDER_PATH)
                  .map((nodeItem, i) => (
                    <DraggableTreeNode
                      index={i}
                      onClick={(e) => handleNodeClick(nodeItem, treeNodes, e)}
                      onCollapse={(e) => toggleNodeExpand(nodeItem, false, e)}
                      onExpand={(e) => toggleNodeExpand(nodeItem, true, e)}
                      nodeItem={nodeItem}
                      isSelected={selectedFolders.find((item) => item.id === nodeItem.id)}
                      handleRenameNode={(val) => handleNodeNameChange(nodeItem, val)}
                      key={i}
                      nodeLength={treeNodes.length}
                    >
                      {renderNodes(nodeItem)}
                    </DraggableTreeNode>
                  ))}
              </ul>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };
  const renderInternalFolder = (designTree) => {
    const node = designTree.children.find((item) => item.fullPath.toLowerCase() === INTERNAL_FOLDER_PATH);
    return node ? (
      <TreeNode
        className="at-tree-node-internal"
        isSelected={selectedFolders.find((item) => item.id === node.id)}
        renderCaret={false}
        isEditable={false}
        onClick={(e) => handleNodeClick(node, designTree.children, e)}
        nodeItem={{ ...node, name: `${node.name} (accessile only to admin)` }}
      >
        {renderNodes(node)}
      </TreeNode>
    ) : (
      <></>
    );
  };
  const handleCollapseAll = () => {
    const newTree = toggleExpandNodes(designTree, false);
    setDesignList({ ...designList, designTree: newTree });
  };
  const handleExpandAll = () => {
    const newTree = toggleExpandNodes(designTree, true);
    setDesignList({ ...designList, designTree: newTree });
  };
  return (
    <div className="at-tree-container" onContextMenu={handleContextMenu}>
      <div className="at-folderlist-toolbar">
        <Button text="Collapse All" icon={IconNames.COLLAPSE_ALL} minimal onClick={handleCollapseAll} />
        <Button text="Expand All" icon={IconNames.EXPAND_ALL} minimal onClick={handleExpandAll} />
      </div>
      <ul className="at-tree-node-list">
        <TreeNode
          isEditable={false}
          renderCaret={false}
          isSelected={selectedFolders.find((item) => item.id === designTree.id)}
          onClick={(e) => handleNodeClick(designTree, [designTree], e)}
          nodeItem={designTree}
        >
          {renderNodes(designTree)}
        </TreeNode>
        {renderInternalFolder(designTree)}
      </ul>
      <DeleteEntryDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => {
          setDeleteDialog({ files: [], isOpen: false });
        }}
        items={deleteDialog.files}
        onDelete={(e) => {
          handleDelete(e);
          setDeleteDialog({ files: [], isOpen: false });
        }}
      />
    </div>
  );
};

export default FolderTree;
